
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { Credentials, amplifyService } from '@kasasa/kloans-common-ui/dist/consumer-app/services/amplify';
import { Auth } from '@kasasa/kloans-common-ui/dist/consumer-app/services/session/types';
import { AxiosResponse } from 'axios';
import { getConsumerStatus, setJwt } from '@kasasa/kloans-common-ui/dist/consumer-app/services/kloans-authentication';
import { GetConsumerStatusResponse } from '@kasasa/kloans-common-ui/dist/consumer-app/services/kloans-authentication/types/ConsumerStatus';
import { requiredOnlyValidation } from '@kasasa/kloans-common-ui/dist/validations';
import { ProductInviteType } from '@/login/enums/ProductInviteType';
import { RawLocation } from 'vue-router';
import { getModule } from 'vuex-module-decorators';
import LoginStore from '@/login/store';
import { store } from '@/store';
import MobileAppReminderModal from '@/login/components/Modal/MobileAppReminderModal.vue';
import { ModalState } from '@/core/types/modal-state';

getModule(LoginStore, store);

@Component({
	components: {
		MobileAppReminderModal,
	},
	validations: {
		credentials: {
			username: requiredOnlyValidation.validations,
			password: requiredOnlyValidation.validations,
		},
	},
})
export default class Login extends Vue {
	@Prop({ default: null })
	profileConnectionToken!: string | null;

	TEST_ID = 'Kloans-Loan-Login';

	loginText = 'Log In';

	$isMobile: any;

	public disableAutopayModalState: ModalState = { isShown: false, isLoading: false };

	credentials: Credentials = { username: '', password: '' };

	validateBtn = true;

	get usernameErrors() {
		const errors: Array<string> = [];
		if (!this.$v.credentials.username?.$dirty) {
			return errors;
		}
		if (!this.$v.credentials.username?.required) {
			errors.push('Username is required.');
		}

		return errors;
	}

	get passwordErrors() {
		const errors: Array<string> = [];
		if (!this.$v.credentials.password?.$dirty) {
			return errors;
		}
		if (!this.$v.credentials.password?.required) {
			errors.push('Password is required.');
		}

		return errors;
	}

	public beforeCreate() {
		this.$store.commit('login/setHeadline', 'Log in with your Kasasa account.');
	}

	public validate() {
		this.$v.credentials.$touch();
		if (!this.$v.credentials.$invalid) {
			this.validateBtn = false;
			this.loginText = 'Logging In...';
			this.credentials.username = this.credentials.username.toLowerCase();
			this.signIn();
		}
	}

	public async signIn() {
		this.$store.dispatch('login/clearError');

		await amplifyService.signIn(this.credentials, {
			onSuccess: this.loginSuccess,
			onFailure: this.loginFailure,
			mfaRequired: this.mfaRedirect,
		});
	}

	async mounted() {
		if (this.$isMobile()) {
			this.launchMobileModal();
		}
	}

	launchMobileModal() {
		this.disableAutopayModalState = { isShown: true, isLoading: true };
	}

	public async loginSuccess() {
		const jwt: string = (localStorage.getItem(Auth.ID_TOKEN) as string);
		setJwt(jwt);

		if (this.profileConnectionToken) {
			await this.redirectToProductInviteViewForProfileConnection();
			return;
		}

		try {
			const response: AxiosResponse<GetConsumerStatusResponse> = await getConsumerStatus() as any;
			const consumerStatus: string = response.data.data.status;

			if (consumerStatus === 'confirmed') {
				this.$router.push({ name: 'updateSecurityQuestions' });
				return;
			}

			await this.$store.dispatch('login/clearError');

			this.$router.push({ name: 'ProfileAccounts' });
			// window.location.href = getCallbackUrl(); Temp comment this out to get the login app at least integrated
			// api calls were being canceled do to the window hard changing the url - JR 11/10/2021
		} catch (error) {
			this.loginFailure('A consumer record cannot be found for this user. Contact Kasasa.');
		}
	}

	private async redirectToProductInviteViewForProfileConnection(): Promise<void> {
		await this.$router.push({
			name: 'kasasaLoanInviteVerification',
			params: {
				token: this.profileConnectionToken ?? '',
			},
			query: {
				type: ProductInviteType.CONNECT_EXISTING_PROFILE.toString(),
			},
		});
	}

	public loginFailure(message: string) {
		this.$store.dispatch('login/showError', message);
		this.loginText = 'Log In';
		this.validateBtn = true;
	}

	public mfaRedirect(): void {
		const mfaRoute: RawLocation = {
			name: 'mfa',
		};

		if (this.profileConnectionToken) {
			mfaRoute.query = {
				token: this.profileConnectionToken,
			};
		}

		this.$router.push(mfaRoute);
	}
}
